import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { BookOpen, ArrowLeft, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
// ZEN-315: the account owner's status definitions are rendered from the SAME
// exported list the Expenses screen's "What do the statuses mean?" panel
// renders. One source, two screens — there is no second copy to drift, which is
// the failure mode ZEN-314 documents. If Expenses.jsx ever stops exporting
// LEGEND, move the list into @/lib/format and import it in both places; do not
// paste the definitions here.
import { LEGEND } from "@/pages/Expenses";
import { StatusChip } from "@/components/StatusChip";

function SetupGuideLink() {
  const { user } = useAuth();
  const qc = useQueryClient();
  if (user?.role !== "owner") return null;
  const reopen = async () => {
    await api.put("/user-prefs", { onboarding_dismissed: false, onboarding_completed_at: null });
    qc.invalidateQueries({ queryKey: ["user-prefs"] });
  };
  return (
    <button onClick={reopen} className="text-xs font-semibold text-[#034AA2] hover:underline" data-testid="reopen-setup-guide">
      Show setup guide
    </button>
  );
}

const ARTICLES = [
  {
    id: "getting-started",
    title: "Getting started for account owners",
    summary: "Set up Zen 529 in five easy steps.",
    body: `
## Welcome to Zen 529

*This is general guidance, not tax advice. Rules change and exceptions apply — confirm your situation with a qualified tax advisor.*

This app helps you track every dollar your family spends on education — so when tax season comes, you already know what's qualified and what's not.

### Five steps to get started

**Step 1** — Add your beneficiary. Go to Beneficiary(ies) and add the person the 529 account is for. You can add more than one if you have multiple kids.

**Step 2** — Add your 529 account. Head to Accounts & Cards. Enter the 529 plan name and the last four digits of the account number. This is just for your records — we never connect to your bank.

**Step 3** — Set the school and cost-of-attendance numbers. Open your beneficiary's card, add the school they attend, and fill in the room & board allowance from the school's published "Cost of Attendance" page. This tells the app the maximum amount that qualifies for 529 funds.

**Step 4** — Invite your beneficiary (optional). If your beneficiary is old enough, you can give them their own login. They can snap receipts and submit expenses directly — but nothing counts until you approve it.

**Step 5** — Approve your first receipt. When your beneficiary submits a receipt, it shows up on your Approvals page. Review it, approve it, and it joins the running tally.

That's it! The app handles the math from here.
    `,
  },
  {
    id: "owner-expense-statuses",
    title: "What the expense statuses mean (for account owners)",
    summary: "To classify, Scheduled, Awaiting, In progress, Reconciled, Closed — where each expense is in its money journey.",
    body: `
Every expense on your Expenses screen carries a status: **To classify**, **Scheduled**, **Awaiting**, **In progress**, **Reconciled**, or **Closed**. Together they tell you where each expense is in its money journey — from "not decided yet" all the way to "settled, with nothing left to do."

You never set a status by hand. The app works it out for you — from the four answers on the expense (**who paid**, **is it 529-eligible?**, **use the 529?**, **reimbursed by whom?**), from the expense's date, and from what has happened to the money since: a 529 distribution matched to it, a Date Received entered, a settlement recorded. Editing those four answers is the usual way to change a status.

Here is what each one means. These are the same definitions shown on the Expenses screen under "What do the statuses mean?" — this article and that panel read from a single shared list, so they cannot fall out of step. Where a definition says "this page," it means the Expenses screen.
    `,
    // Rendered between body and bodyAfter: the shared status list, live from
    // the Expenses screen module.
    statusList: true,
    bodyAfter: `
### If your beneficiary also uses the app

The receipts your beneficiary submits to you have their own, shorter status list — Draft, Submitted, Approved, Returned — described in [[student-receipts|A beneficiary's guide to submitting receipts]]. Their **Draft** is a receipt they've saved but not yet sent to you. Once you approve a receipt, it becomes an expense on your Expenses screen, under **To classify**, until you answer whether it's 529-eligible.
    `,
  },
  {
    id: "student-receipts",
    title: "A beneficiary's guide to submitting receipts",
    summary: "Snap, submit, and track your expenses.",
    body: `
## How to submit receipts

Your parent (or whoever manages the 529 account) has given you a login. Here's how the process works.

### Three simple steps

1. **Snap or upload.** Take a photo of your receipt in My Receipts, or upload a file you already have. Either way it lands in your drafts.
2. **Fill in the details.** Add the store name, amount, date, and a short note about what it was for. You don't pick a category or decide anything about tax — your parent does that part. Submit it.
3. **Wait for approval.** Your parent sees it on their Approvals page. They'll approve it, return it for edits, or reject it.

### What the statuses mean

- **Draft** — You saved it but haven't submitted yet. Still editable.
- **Submitted** — Waiting for your parent to review.
- **Approved** — Your parent accepted it into their records. They still decide whether it's a 529 expense, so you may not see it counted anywhere yet.
- **Returned** — Your parent sent it back with a note. Fix whatever they asked and resubmit.

### Good to know

Nothing you submit changes the family's tax numbers until your parent approves it. So don't worry about making a mistake — it's a draft until you submit, and it's not final until they approve.
    `,
  },
  {
    id: "qualified-expenses",
    title: "What counts as a 529 qualified expense",
    summary: "What's qualified depends on the beneficiary's expense group.",
    body: `
## What counts as a 529 qualified expense

What's qualified depends on the beneficiary's expense group — here's what each covers, and where it applies. Throughout the app these are labeled 529-eligible; the IRS calls them qualified expenses — same thing.

### Higher Education

Applies at an eligible postsecondary institution — a college, university, or vocational school eligible for federal student aid (Title IV). These appear on the U.S. Department of Education's Federal School Code List, which is what this app checks to show the green "529-eligible" badge when you add a school to a beneficiary.

Qualified: **tuition and fees**; **books**; **supplies and equipment**; **computers and technology** (used primarily by the beneficiary); **room and board — housing and food, including groceries and restaurant meals**: qualifies only while the beneficiary is enrolled at least half-time, and only up to the greater of the school's published cost-of-attendance room-and-board allowance or, for school-owned housing, the amount the school actually charges; and **special-needs services**.

### K-12 Education

Tuition and related costs at any public, private, or religious elementary or secondary school — no Federal School Code needed. Capped at $20,000 per beneficiary per year (2026).

Qualified: **tuition**; **curriculum**; **books**; **online educational materials**; **tutoring**; **disability therapy**; **dual-enrollment fees**; **standardized test fees**.

### Apprenticeship

Applies to a program registered with the U.S. Secretary of Labor (a registered apprenticeship) — verify on the Department of Labor's registered-apprenticeship listing.

Qualified: **fees**; **books**; **supplies and equipment**. No tuition — apprenticeships don't charge it.

### Postsecondary Credentialing

Applies to a recognized postsecondary credential program — for example, on a state Workforce Innovation and Opportunity Act (WIOA) eligible-training list, the Department of Veterans Affairs Web Enabled Approval Management System (WEAMS), the Department of Defense Credentialing Opportunities On-Line (COOL), an industry-accredited program, or one leading to a recognized state or federal occupational license.

Qualified: **tuition and fees**; **books**; **supplies and equipment**; **required testing fees**; **continuing-education fees**.

### Additional allowable uses

No school here:

- **Student loan repayments** — up to $10,000 lifetime per person (each sibling has their own $10,000). Record these as a 529 distribution (under 529 Distributions), not as an expense.
- **Roth IRA rollovers** — unused 529 funds, up to $35,000 lifetime, if the account has been open at least 15 years. Record these as a 529 distribution (under 529 Distributions), not as an expense. Rolling over more than the annual limit (that year's Roth IRA contribution limit) or the $35,000 lifetime is treated as an excess Roth IRA contribution, subject to a 6% excise tax each year until corrected — distinct from the 10% penalty on non-qualified 529 distributions.

### The "required" rule

For most categories the key word is required: if the school or program requires it for enrollment or a course, it's probably qualified; if it's optional or personal, it's not.

### Expenses that never qualify

These never count as qualified 529 expenses, even when the school requires them:

- Transportation — a car, gas, flights home, parking, transit passes
- Health insurance and medical costs — student health insurance, student health fees, and general medical expenses. These never qualify, even when the school requires them as a condition of enrollment.
- Personal and living expenses above the 529 limit for housing and food — cell phone plans, clothing, entertainment
- Extracurricular, activity, and sports fees not required for enrollment — including school sports teams

Important: these are the federal rules. States set their own, and not all conform — an expense can be qualified federally and still be taxable in your state (K-12 tuition is the most common gap). Check your state's rules before withdrawing. You'll find the details in this Help Center under [[state-conformity|How your state's rules can differ]].
    `,
  },
  {
    id: "aotc",
    title: "The American Opportunity Tax Credit (AOTC)",
    summary: "What it is, who qualifies, and how it works with a 529 plan.",
    body: `
## The American Opportunity Tax Credit (AOTC)

*This is general guidance, not tax advice. Rules change and exceptions apply — confirm your situation with a qualified tax advisor.*

The American Opportunity Tax Credit (AOTC) is a federal education tax credit worth up to **$2,500 per student per year**. The credit is calculated as 100% of the first $2,000 plus 25% of the next $2,000 of qualified expenses — tuition, required enrollment fees, and required course materials (books, supplies, and equipment needed for a course of study, whether or not purchased from the school). Room and board, transportation, insurance, and personal expenses do **not** qualify for the credit.

Up to 40% of the credit (up to $1,000) is refundable — meaning you can receive it even if you owe no federal tax that year. The refundable portion does not apply to a student in the [[kiddie-tax|kiddie tax]] profile (generally under 18, or a full-time student 19–23 whose earned income is less than half their own support).

### Who qualifies

- The student must be in their **first four years** of postsecondary education (higher education only — K-12, apprenticeship, and credentialing are not eligible).
- The credit can be claimed for **no more than four tax years** total per student.
- The student must be enrolled **at least half-time** for at least one academic period during the tax year.
- The student must be pursuing a degree or other recognized credential.
- **Modified Adjusted Gross Income (MAGI) phase-out:** the credit begins to phase out at $80,000 and is fully phased out at $90,000 for single / head of household filers, and $160,000–$180,000 for married filing jointly. These thresholds are statutory and not adjusted for inflation. **Married filing separately cannot claim the AOTC.**
- Starting with **tax year 2026**, both the person claiming the credit and the student must have a Social Security Number (SSN) valid for work. An Individual Taxpayer Identification Number (ITIN) can no longer be used for either.

### How the AOTC interacts with a 529 plan

The same expenses cannot be used for **both** a tax-free 529 withdrawal **and** the AOTC — this is the IRS "no double-dip" coordination rule.

In practice, this means: if you pay up to $4,000 of qualified tuition, fees, and course materials **out of pocket** (without drawing from the 529), you can claim up to $2,500 of AOTC on those expenses. That $2,500 credit is usually worth more than the tax savings on $4,000 of 529 earnings — so most families come out ahead by reserving expenses for the credit.

The expenses you reserve for the AOTC reduce your Adjusted Qualified Education Expenses (AQEE), which means a portion of your 529 distribution may become taxable. However, the **10% additional tax is waived** on the portion that's taxable only because you claimed the credit (IRS Publication 970, Chapter 7, Exception 5).

### How it works in Zen 529

Set your per-student, per-year AOTC eligibility and reserve amount on the **Beneficiary(ies) page beneficiary card** (under "AOTC"). When the beneficiary card says Yes to the AOTC for a tax year, "Use the 529 for this expense?" offers a third answer for tuition, required fees and course materials: "Partly — reserve for the AOTC first, use 529 funds for the rest". It appears only while part of the AOTC reserve on the beneficiary card has not yet been reserved by other expenses. Choosing it reserves as much of this expense as the AOTC reserve still has available, up to the whole cost, and the 529 can pay the rest. For example, if the AOTC reserve is $4,000 and other expenses already hold $3,000, a $2,500 tuition gets $1,000 reserved and the 529 can pay the other $1,500. The same dollar never counts for both. While an expense's exchange rate is still an estimate, this answer waits for the official rate, unless you enter the rate by hand.

### Related articles

- [[aotc-expenses|AOTC: which expenses you can reserve for the tax credit]]
- [[when-not-to-draw|When not to draw from your 529]]
- [[scholarships-529|Scholarships and your 529]]
- [[non-qualified-distributions|Qualified vs. non-qualified 529 distributions]]

Reference: IRS Publication 970, Chapters 2 and 7.
    `,
  },
  {
    id: "aotc-expenses",
    title: "AOTC: which expenses you can reserve for the tax credit",
    summary: "The AOTC has its own, narrower qualified-expense list than a 529.",
    body: `
## AOTC: which expenses you can reserve for the tax credit

The American Opportunity Tax Credit (AOTC) covers a narrower set of expenses than a 529 plan. Paying these costs out of pocket instead of drawing from the 529 lets you claim up to $2,500 of tax credit per student per year — usually worth more than the tax savings on $4,000 of 529 earnings. Zen 529 helps you reserve expenses for the AOTC, but this is general guidance, not tax advice — confirm the details with your tax advisor.

### What the AOTC covers

The AOTC covers **only** tuition, required enrollment fees, and course materials — books, supplies, and equipment needed for a course of study, whether or not purchased from the school. It does **not** cover room and board, transportation, insurance, or personal expenses.

### 529 vs. AOTC by category

| Category | 529 qualified? | AOTC qualified? |
|---|---|---|
| Tuition & Fees | Yes | Yes |
| Books | Yes | Yes |
| Supplies / Equipment | Yes | Yes |
| Computers / Technology | Yes | Only if needed for a course of study |
| Room & Board | Yes (with limits) | No |
| Housing | Yes (with limits) | No |
| Food | Yes (with limits) | No |
| Special Needs Services | Yes | No |

### Student-level conditions

The AOTC applies only to **higher education** (postsecondary degree-seeking programs). It is not available for K-12, apprenticeship, or credentialing enrollments. Additional conditions:

- The student must be in their **first four years** of postsecondary education.
- The credit can be claimed for **no more than four tax years** total per student.
- The student must be enrolled **at least half-time** for at least one academic period during the year.
- The credit phases out at MAGI $80,000\u2013$90,000 (single) and $160,000\u2013$180,000 (married filing jointly). Unavailable for married filing separately.
- Starting with **tax year 2026**, the student must have a Social Security Number (SSN) valid for work, and the school must provide its Employer Identification Number (EIN). An Individual Taxpayer Identification Number (ITIN) can no longer be used for the student.

### How it works in the app

When the beneficiary card says Yes to the AOTC for a tax year, "Use the 529 for this expense?" offers a third answer for tuition, required fees and course materials: "Partly — reserve for the AOTC first, use 529 funds for the rest". It appears only while part of the AOTC reserve on the beneficiary card has not yet been reserved by other expenses. Choosing it reserves as much of this expense as the AOTC reserve still has available, up to the whole cost, and the 529 can pay the rest. For example, if the AOTC reserve is $4,000 and other expenses already hold $3,000, a $2,500 tuition gets $1,000 reserved and the 529 can pay the other $1,500. The same dollar never counts for both. While an expense's exchange rate is still an estimate, this answer waits for the official rate, unless you enter the rate by hand.

Set your per-student, per-tax-year AOTC reserve target on the **Beneficiary(ies) page beneficiary card** (under "AOTC"). See [[aotc|The American Opportunity Tax Credit (AOTC)]] for the full overview.

### Related articles

- [[qualified-expenses|What counts as a 529 qualified expense]]
- [[when-not-to-draw|When not to draw from your 529]]
- [[scholarships-529|Scholarships and your 529]]
    `,
  },
  {
    id: "same-year-rule",
    title: "The same-calendar-year rule",
    summary: "The year you pay a cost is the year to take the money out.",
    body: `
## Take the money out in the same calendar year you pay the expense

This is the one that trips up the most families, and it is worth ten minutes of
your time once.

When you take money out of a 529 account, you want to be able to show that you also
paid qualifying education costs in that **same calendar year** — 1 January to 31
December. Not the school year. Not the term. The calendar year.

### How it works

Withdraw $10,000 from your 529 in 2025, and you want at least $10,000 of
qualifying costs that were also **paid** in 2025. Where they fall inside the year
does not matter: a March withdrawal can cover a cost you paid in October, as long
as both land in the same calendar year.

### The date you paid is the date that counts

Not the date on the invoice. Not the date the term starts. **The day the money
actually left your hands.**

That is why this app works out the tax year from the payment date you enter,
rather than asking you to choose one. One payment date, one tax year — there is
nothing to pick, and nothing to get wrong.

### The December-to-January trap

Watch the semester boundary. Spring tuition is often billed in December.

- **Pay it in December** → withdraw from the 529 in December.
- **Pay it in January** → withdraw in January.

Follow the payment, not the invoice and not the term. If the payment and the
withdrawal end up in different calendar years, the Internal Revenue Service could
decide the withdrawal does not get the tax treatment you were expecting.

### A cost due on 1 January

Rent or a fee due on 1 January belongs to the new tax year, even though you see
it coming in December.

- **It is best not to withdraw for it in December.** A December withdrawal and a January payment would land in different calendar years.
- **A simple way to keep them together:** pay it on its due date from your own money, then withdraw in January. Both then fall in the same calendar year.

This is why, in December, the "Coming up" card on the Overview stops at 31
December. Costs due in January appear there once January starts.

### Best practices when taking money out

- **Add up the year's qualifying costs first.** Before withdrawing, total what you
  have paid, or expect to pay, in that calendar year.
- **Take the money in the same window you pay.** Same month is ideal; the same
  calendar year is what matters.
- **Keep the receipt and the invoice.** How long to hold them is a question for
  your accountant — it depends on your own circumstances, and this app does not
  give you a number it cannot stand behind.

### About the "grace period" you may have heard about

The Internal Revenue Service once **proposed** a rule that would have let a
withdrawal count for costs paid up to 31 March of the following year
(Announcement 2008-17). **That proposal was never finalised.** Some tax
professionals talk about it as though it exists; it does not.

Section 529 of the tax code says nothing at all about timing. So the
same-calendar-year approach is not a rule anyone can quote at you — it is the
safest, best-documented position, which is why this app steers you there and
tells you when something falls outside it. It flags; it does not decide. Anything
in the grey area is a conversation with your tax adviser.
    `,
  },
  {
    id: "room-board-cap",
    title: "Room & board and the COA cap",
    summary: "How the cost-of-attendance limit works for housing and food.",
    body: `
## Room & board: the cost-of-attendance cap

Unlike tuition, room and board spending from a 529 is capped. The cap is based on the school's published "Cost of Attendance" (COA) — the number the financial aid office uses to estimate total costs.

### Off-campus beneficiaries

If your beneficiary lives off campus (an apartment, at home, etc.), the qualified amount for room and board is limited to the school's COA allowance for that living arrangement. Spend more than that, and the extra isn't qualified.

### School-owned housing (dorms, meal plans)

Here's where it gets more generous. IRS Publication 970 says the qualified amount is the **greater of**:
- The school's COA allowance, OR
- The actual amount the school charged for housing and the meal plan

So if the school charges $13,000 for a dorm and meal plan but the COA allowance is only $12,000, you can claim the full $13,000.

### The headroom concept

The gap between the school's charges and the cap is "headroom" — additional room & board spending (like groceries or restaurant meals) that can still qualify. The app calculates this for you automatically.

### What to enter in the app

On your beneficiary's school settings, enter the COA allowance per month or per term. If they're in school housing, also enter the actual amount invoiced by the school. The app picks the higher number and uses it as the cap.
    `,
  },
  {
    id: "scholarships-529",
    title: "Scholarships and your 529",
    summary: "How tax-free aid affects your 529 qualified amounts.",
    body: `
## How scholarships interact with your 529

If your beneficiary receives a scholarship, grant, or other tax-free educational assistance, it changes how much the 529 can cover.

### The basic rule

Tax-free aid reduces the amount of expenses that count as "qualified" for 529 purposes. If your beneficiary has $20,000 in tuition and receives a $5,000 scholarship, only $15,000 of that tuition is qualified for 529 funds.

### The penalty waiver

Normally, if you withdraw more from a 529 than you have in qualified expenses, the earnings portion of the excess gets hit with a 10% penalty plus income tax. But there's an exception: you can withdraw up to the scholarship amount without the 10% penalty. You'll still owe income tax on the earnings, but the penalty is waived.

### What to do in the app

Go to Scholarships & Aid and enter each scholarship or grant. The app adjusts the "Adjusted Qualified Education Expenses" (AQEE) math automatically and shows you the impact.

If you are also claiming the AOTC, see [[aotc-expenses|AOTC: which expenses you can reserve for the tax credit]] — a family reimbursement doesn't affect the credit, but tax-free scholarships and grants do.
    `,
  },
  {
    id: "irs-letter",
    title: "If an IRS letter arrives",
    summary: "Don't panic — here's what to do and how the app helps.",
    body: `
## Got a letter from the IRS about your 529?

Take a breath. This is common and usually not a problem.

### Why it happens

When your 529 plan sends money to you or your school, it files a Form 1099-Q with the IRS. When the school reports tuition received, it files a Form 1098-T. The IRS tries to match them — and sometimes the numbers don't line up on their first pass, especially if expenses and withdrawals crossed calendar years.

A frequent trigger: the 1099-Q is issued under the parent's Social Security number when the distribution was paid to the parent, while the 1098-T is under the student's — so the IRS computer can't match them automatically. Distributions paid directly to the student or the school are reported under the student's SSN and attract less of this scrutiny.

### What to do

1. Read the letter carefully. It usually asks you to verify that you had enough qualified expenses to cover the withdrawal.
2. Pull your year-end report from this app. It shows every expense, withdrawal, and how they match up — exactly the story the IRS is looking for.
3. Respond by the deadline. Follow the letter's instructions. In most cases, you're just confirming what you already know.

### How this app helps

The Money Trace page and the year-end export (CSV or PDF) show the full audit trail: every qualified expense, every withdrawal, and the reconciliation between them. This is the documentation the IRS wants to see.
    `,
  },
  {
    id: "record-keeping",
    title: "What records to keep, and for how long",
    summary: "Receipts, forms, and how long the IRS says to hold onto them.",
    body: `
## Record-keeping for 529 expenses

Good records protect you. If the IRS ever asks, you want to be able to show exactly what you spent and why it was qualified.

### What to keep

- **Receipts** for every expense you claim as qualified — tuition invoices, bookstore receipts, rent payments, meal plan invoices.
- **Form 1098-T** from the school (tuition statement).
- **Form 1099-Q** from the 529 plan (distribution statement).
- **Scholarship and grant letters** showing amounts and terms.
- **Cost of Attendance documentation** — a screenshot of the school's published COA page for each year.
- **Your year-end reconciliation** from this app (the PDF export).

### How long to keep them

The IRS generally recommends keeping tax records for **3 years** from the date you filed the return. But for 529 accounts, many tax professionals recommend keeping records for as long as the account is open, plus 3 years after the last distribution. That's because the IRS can ask about any year a distribution was made.

### This app helps

Upload supporting documents directly to each term's settings. Use the year-end export to create a complete record. The app keeps everything organized so you don't have to dig through shoeboxes later.
    `,
  },
  {
    id: "foreign-currency",
    title: "Foreign currency and the exchange rate",
    summary: "How foreign expenses are converted to USD and when to use manual override.",
    body: `
## Which day's exchange rate?

When you pay a qualified expense in a foreign currency — for example, Canadian dollars at a Canadian university — it has to be converted to US dollars to match your 529 withdrawal. The IRS rule is simple: use the exchange rate on the expense's transaction date. Not an annual average, and not the day you took the 529 withdrawal — the transaction date.

When the rate source has no rate for that exact date (a weekend or public holiday), the most recent rate published **on or before** the transaction date is used — never a later date. For example, a Saturday expense uses Friday's published rate.

## How this app does it for you

When you enter an expense in a foreign currency, the app automatically looks up the published exchange rate for the transaction date and locks it on the expense, along with the date and the source. That locked rate is your audit evidence, captured the moment you record the expense — far more defensible than reconstructing a rate years later.

**Future-dated expenses:** when the transaction date is still in the future, that day's rate doesn't exist yet, so the app shows an estimate from the most recent available rate, marked "Awaiting final exchange rate." The estimate refreshes as new rates are published and locks automatically once the rate that applies to the transaction date is published. An expense entered on its own day before that day's rate is out (the European Central Bank publishes once each business day, mid-morning in North America) shows as an estimate for those few hours in the same way. Until it locks, the estimated amount is **not** counted in tax figures or available for withdrawal allocation.

## Where the rate comes from

The app uses one source for everyone: the European Central Bank's daily reference rates, stored inside the app and locked onto each expense at its transaction date. The IRS has no official exchange rate — it accepts any consistently-used posted rate — and using a single published source across the whole tax year is exactly that. Anyone (including an auditor) can verify a stored rate against the ECB's own published table for the same date. Non-euro pairs (like CAD to USD) are the standard cross-rate from the ECB's two published figures. For the handful of currencies the ECB doesn't publish, the app automatically falls back to the official US Treasury reporting rate for conversion to USD — you don't choose or configure this. A currency neither source can rate prompts you for a manual rate.

## When to use "Manual override"

Sometimes you have a better record of the actual rate than a published daily average. The most common case: you paid the foreign expense with a US credit card, and your statement shows the exact US-dollar amount that was charged. That real, documented number is the most defensible figure of all. Turn on Manual override, enter the rate (or the US-dollar amount), and attach the statement to the expense so the number is backed by a document. A manual override is always final — it is never treated as an estimate, even on a future-dated expense. If your tax advisor prefers a specific rate methodology, this is the way to apply it.

## Keeping it audit-ready

- For automatic rates, the app already stores the rate, date, and source — nothing more to do.
- For manual rates, attach the supporting document (the card or bank statement) to the expense.
- Either way, keep withdrawals and expenses in the same calendar year (see "The same-calendar-year rule").

Reference: IRS, "Foreign currency and currency exchange rates" — "use the exchange rate prevailing when you receive, pay, or accrue the item." Also IRS Publication 970.
    `,
  },
  {
    id: "state-conformity",
    title: "How your state's rules can differ",
    summary: "Federal rules are uniform — state rules aren't.",
    body: `
## How your state's rules can differ

529 plans are federal, so what counts as qualified — and stays free of federal tax — is the same everywhere. But states set their own income-tax rules, and not all of them "conform" to the federal list. The most common gap is K-12 tuition: federally qualified, but about a dozen states (including California, New York, and Colorado) don't recognize it — there, a K-12 withdrawal is non-qualified for state tax, so the earnings can be taxed and any state deduction you claimed can be clawed back. Student loans, apprenticeship, and Roth rollovers have similar state-by-state gaps. An expense can be fully qualified federally and still create a state tax bill.

Which state's rules apply? The one where you file your income taxes — your state of residence — not the state whose 529 plan you opened. State tax on a withdrawal is charged by your home state, using its own list of what's qualified. (Your plan's home state matters for something else: whether you got a state tax deduction when you contributed — many states only allow that for their own plan.) If you've moved since opening the account, check both: a state that gave you a contribution deduction can reclaim it when a withdrawal is non-qualified there.

This app tracks the federal rules; before withdrawing in a category your state may not follow, check your state's 529 rules or ask your tax advisor.

### Recapture of past state deductions

Many states give a state income-tax deduction for contributions to their own 529. If you claimed that deduction and later take a non-qualified withdrawal, the state can "recapture" it — add it back to your state taxable income. It only applies if you actually claimed the deduction, only in that state, and generally only while you still file taxes there — so it may not apply if you bought the plan while living elsewhere, never claimed the deduction, or have since moved away. The app flags this at withdrawal time based on the filing state you set in Settings. Your situation depends on your state's rules — confirm with your tax advisor.
    `,
  },
  {
    id: "withdrawal-recipient",
    title: "Whose name should the 529 distribution be in?",
    summary: "Owner, beneficiary, or school — the choice affects 1099-Q matching and who gets taxed.",
    body: `
## Whose name should the 529 distribution be in?

When you take money out of a 529, it can be paid to the school directly, to the beneficiary, or to you (the owner). The IRS doesn't require any one of these — but the choice matters, because whoever receives the money gets Form 1099-Q in their name and Social Security number.

The cleanest choice is usually the school directly, or the beneficiary:

- The 1099-Q (in the student's name) then matches the 1098-T the school sends (also in the student's name), so everything lines up on the student's return. Paying yourself as owner creates a mismatch — the IRS sees a distribution in your name but the education expenses under the student — which is more likely to trigger an automated notice.
- If any part of a withdrawal is taxable (non-qualified, or more than the qualified costs), the earnings are taxed at the recipient's rate. The beneficiary's is usually lower than yours — though see "The kiddie tax" below.

When owner-paid makes sense: the reimbursement approach — you pay an expense out of pocket, then reimburse yourself from the 529. That's common and fine; just keep good records, since the 1099-Q will be in your name.

For fully qualified withdrawals there's usually no tax and little practical difference — the main reason to favor school-direct or the beneficiary is cleaner IRS matching. Confirm the right approach for your family with your tax advisor.
    `,
  },
  {
    id: "kiddie-tax",
    title: "The kiddie tax",
    summary: "How the kiddie tax applies to 529 earnings taxed in a beneficiary's name.",
    body: `
## The kiddie tax

The "kiddie tax" stops families from shifting investment income to a child to use the child's lower tax bracket. It applies to a child's unearned income — interest, dividends, capital gains, and the taxable earnings portion of a non-qualified 529 withdrawal when the student is the recipient. It does not apply to a job's wages.

For 2025 and 2026, a dependent student's unearned income is taxed in three layers: the first $1,350 is tax-free; the next $1,350 (up to $2,700) is taxed at the student's own low rate; anything above $2,700 is taxed at the parents' marginal rate. It applies to students under 18, or 19–23 if they're full-time students (and don't provide more than half their own support). These thresholds adjust for inflation, so check the current year's figures.

Why it matters here: if a 529 withdrawal creates taxable earnings and the student is the recipient, only about the first $2,700 gets the student's low rate — beyond that it's taxed at your rate anyway. For fully qualified withdrawals there are no taxable earnings, so the kiddie tax never comes up.
    `,
  },
  {
    id: "when-not-to-draw",
    title: "When not to draw from your 529",
    summary: "Some costs are better paid another way \u2014 and some can\u2019t use the 529 at all.",
    body: `
## When not to draw from your 529

Sometimes it\u2019s better not to draw the 529 for a cost:

- Preserve the American Opportunity Tax Credit (AOTC). You can't use the same expenses for both a tax-free 529 withdrawal and the AOTC. Paying up to $4,000 of tuition, fees, and course materials out of pocket lets you claim up to $2,500 of AOTC — usually worth more than the tax-free earnings on that $4,000. See [[aotc-expenses|AOTC: which expenses you can reserve for the tax credit]] for the full category breakdown.
- Above a cap. If a cost is above a 529 limit (for example the $20,000/year K-12 tuition cap or the $10,000 lifetime student-loan limit), the excess isn't qualified — don't draw the 529 for it.
- Covered by a tax-free scholarship. Expenses the scholarship already pays for aren't 529-eligible. You can still withdraw up to the scholarship amount without the 10% penalty — but the earnings on it remain taxable.

This app flags these situations but doesn't give personalized tax advice — talk to your tax advisor about the right mix.
    `,
  },
  {
    id: "non-qualified-distributions",
    title: "Qualified vs. non-qualified 529 distributions",
    summary: "How they differ, what a non-qualified one costs, and better options.",
    body: `
## Qualified vs. non-qualified 529 distributions

Every dollar you take out of a 529 is a \u201cdistribution.\u201d Each one is either **qualified** \u2014 the earnings come out tax-free \u2014 or **non-qualified** \u2014 the earnings are taxed, usually plus a 10% additional tax.

### What makes a distribution qualified

The money goes to one of these:

- **Qualified education expenses** you paid that year \u2014 tuition, fees, books, required equipment, and room and board within the school\u2019s limits.
- A **student-loan repayment** \u2014 up to $10,000 in your lifetime, per person.
- A **Roth IRA rollover** \u2014 up to $35,000 in your lifetime, if the 529 has been open at least 15 years and other conditions are met.

As long as your distributions for the year don\u2019t exceed those qualified amounts, the earnings are completely tax-free.

### What makes a distribution non-qualified

Anything that doesn\u2019t fit the above. The common reasons:

- You **withdrew more than your qualified expenses** for the year.
- You used the money for a **cost a 529 can\u2019t cover** \u2014 transportation, insurance, and other non-eligible costs.
- You withdrew for expenses **already covered by a tax-free scholarship**.

### What a non-qualified distribution costs

- **Your contributions are never taxed and never penalized.**
- **Only the earnings portion** is taxed \u2014 as ordinary income to whoever received the money, **plus a 10% additional tax** (commonly called the 10% penalty).

So, it\u2019s income tax plus the 10% additional tax on the **earnings** only.

### When the 10% additional tax is waived

The 10% additional tax doesn\u2019t apply in these cases \u2014 but the earnings are **still** subject to income tax (the exception removes the additional tax, not the income tax):

- The beneficiary\u2019s **death** (paid to the beneficiary or their estate).
- The beneficiary\u2019s **disability** (physician-certified).
- Up to the amount of a **tax-free scholarship** (also veterans\u2019 or employer educational assistance).
- Attendance at a **U.S. military academy** (up to the costs of advanced education).
- Expenses used to claim the **American Opportunity or Lifetime Learning Credit**.

### The education-credit interaction

You can\u2019t count the same expenses for both a tax-free 529 withdrawal and the American Opportunity Tax Credit (AOTC). Two clean ways to get the credit:

- **Pay out of pocket:** cover up to $4,000 of tuition, fees, and course materials yourself and don\u2019t draw the 529 for it \u2014 you claim up to $2,500 of AOTC and create no taxable distribution.
- **Draw the 529, then reserve for the AOTC:** the $4,000 you reserve becomes taxable on its earnings, but the **10% additional tax is waived**. Either way you get the same credit.

### Better options before taking a non-qualified distribution

- **Use it for a qualified expense** if the cost actually qualifies.
- **Roth IRA rollover** of unused funds \u2014 up to $35,000 lifetime, conditions apply. No income tax or additional tax.
- **Student-loan repayment** \u2014 up to $10,000 lifetime per person.
- **Change the beneficiary** to another eligible family member.
    `,
  },
  {
    id: "family-money-tools",
    title: "What a 529 doesn\u2019t cover \u2014 and how to track those costs",
    summary: "How Family Money Tools helps you track everyday costs that a 529 can\u2019t pay for.",
    body: `
## What a 529 doesn\u2019t cover \u2014 and how to track those costs

These everyday costs are real, and families often want them in one place instead of scattered across texts and spreadsheets. That\u2019s what Family Money Tools is for \u2014 logging the money that flows around school life that a 529 can\u2019t pay for, so everyone can see who paid for what.

This tool set is completely optional. You never have to use it. It\u2019s there if it helps, and ignored with no downside if it doesn\u2019t.

It never mixes with your tax records. Anything you track in Family Money Tools stays walled off from your 529 reports, your qualified-expense math, and your CPA and audit packets. It cannot inflate a qualified withdrawal, cannot appear in a 1099-Q reconciliation, and cannot change any tax figure. It\u2019s a household convenience, not a tax record \u2014 and it stays that way.
    `,
  },
  {
    id: "k12-limit",
    title: "What\u2019s the 529 limit for K-12 expenses?",
    summary: "The annual per-beneficiary cap for K-12 qualified expenses.",
    body: `
## What\u2019s the 529 limit for K-12 expenses?

For 2026, up to **$20,000 per beneficiary** each year can go toward qualified K-12 expenses tax-free (up from $10,000 in 2025). The limit is per beneficiary and covers the combined withdrawals from all of their 529 accounts that year \u2014 a second account doesn\u2019t raise it.

(Based on the 2025 law (OBBBA); the IRS hasn\u2019t issued updated guidance yet, so confirm with your tax preparer.)
    `,
  },
  {
    id: "per-beneficiary-limits",
    title: "Do 529 limits apply per account or per beneficiary?",
    summary: "Every 529 tax limit is measured against the beneficiary, not the account.",
    body: `
## Do 529 limits apply per account or per beneficiary?

**Per beneficiary.** Every 529 tax limit is measured against the beneficiary, not the account \u2014 so if a child has more than one 529 (say, one from a parent and one from a grandparent), the limits apply to their combined withdrawals, not to each account separately.
    `,
  },
  {
    id: "1099q-records",
    title: "What is Form 1099-Q, and what records should I keep?",
    summary: "What the form reports and the records you should keep alongside it.",
    body: `
## What is Form 1099-Q, and what records should I keep?

Form 1099-Q reports the money distributed from your 529 account for the year. You don\u2019t file it \u2014 or your supporting records \u2014 with your return, but keep them in case the IRS asks: receipts and invoices for each expense, enrollment or transcript records, the school\u2019s published cost of attendance (COA) and its room-and-board allowance, and proof of at least half-time enrollment for any room-and-board claim. Your audit packet gathers these for each tax year.
    `,
  },
  {
    id: "glossary",
    title: "Glossary: the abbreviations this app uses",
    summary: "What each short form you see on screen stands for.",
    body: `
## Glossary: the abbreviations this app uses

- **529** \u2014 A 529 plan: a tax-advantaged savings account for education, named after section 529 of the US tax code.
- **AOTC** \u2014 American Opportunity Tax Credit: a federal tax credit of up to $2,500 per student per year, for the first four years of higher education.
- **AQEE** \u2014 Adjusted Qualified Education Expenses: the year's qualified education expenses after subtracting tax-free aid, such as scholarships, and any expenses set aside for a tax credit.
- **COA** \u2014 Cost of Attendance: the school's published estimate of what a year costs. It sets the limit on room and board paid from a 529 account.
- **R&B** \u2014 Room and board: housing and food.
- **1099-Q** \u2014 The form a 529 plan sends each year showing the money taken out of the account.
- **1098-T** \u2014 The form US schools send each year showing tuition and scholarships.
- **TY** \u2014 Tax year.
- **IRS** \u2014 Internal Revenue Service: the US federal tax agency.
- **Pub 970** \u2014 IRS Publication 970, "Tax Benefits for Education".
- **MAGI** \u2014 Modified Adjusted Gross Income: the income figure the IRS uses to decide whether you can claim an education tax credit.
- **SSN** \u2014 Social Security Number.
- **ITIN** \u2014 Individual Taxpayer Identification Number: a tax number the IRS issues to people who cannot get a Social Security Number.
- **EIN** \u2014 Employer Identification Number: a school's federal tax number.
- **FAFSA** \u2014 Free Application for Federal Student Aid.
- **K-12** \u2014 Kindergarten through 12th grade.
- **OBBBA** \u2014 One Big Beautiful Bill Act: the 2025 federal law that changed several 529 rules.
- **FX** \u2014 Foreign exchange: converting an amount in another currency into US dollars.
- **ECB** \u2014 European Central Bank: the source of the app's daily exchange rates.
- **USD, CAD and other three-letter codes** \u2014 Currency codes. USD is US dollars; CAD is Canadian dollars.
- **OCR** \u2014 Optical character recognition: reading the text of a photo or scan automatically.
- **AI** \u2014 Artificial intelligence.
- **CSV, PDF, HEIC** \u2014 File types: a spreadsheet file, a document file, and the photo format iPhones use.
    `,
  },
  {
    id: "lost-device",
    title: "Lost a phone or left a computer signed in?",
    summary: "Change your password.",
    body: `
## Lost a phone or left a computer signed in?

Change your password. On the sign-in page choose "Forgot password?", follow the link we email you, and set a new password. Every other device is signed out at once and will need the new password to get back in.
    `,
  },
  {
    // Title, summary and paragraph approved by Maryse (ZEN-692, 2026-09-25
    // and 2026-10-14).
    id: "archiving-a-beneficiary",
    title: "Archiving a beneficiary",
    summary: "What stops being counted when you archive, and what to settle first.",
    body: `
## Archiving a beneficiary

Archiving a beneficiary stops all of their unfinished work being counted — waiting receipts, expenses needing a decision, and warnings about 529 money already drawn. Settle everything and file for the years involved before you archive; their records are kept, and you can reactivate them at any time.
    `,
  },
];

const DISCLAIMER = "This app is a record-keeping tool, not tax or legal advice. For tax questions specific to your situation, consult a qualified tax professional.";
const IRS_PUB_970 = "https://www.irs.gov/publications/p970";

export default function Help() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeId, setActiveId] = useState(null);
  const article = ARTICLES.find((a) => a.id === activeId);

  useEffect(() => {
    const paramArticle = searchParams.get("article");
    if (paramArticle && ARTICLES.some((a) => a.id === paramArticle)) {
      setActiveId(paramArticle);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (article) {
    return (
      <div>
        <PageHeader title="Help Center" actions={<SetupGuideLink />} />
        <div className="mx-auto max-w-2xl">
          <button onClick={() => setActiveId(null)} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-[#034AA2] hover:underline" data-testid="help-back">
            <ArrowLeft size={16} weight="bold" /> All articles
          </button>
          <article className="prose prose-sm max-w-none" data-testid={`help-article-${article.id}`}>
            <h1 className="text-xl font-extrabold text-[#0A0A0A]">{article.title}</h1>
            <div className="mt-4 space-y-3 text-sm leading-relaxed text-[#374151]">
              {renderArticleBody(article.body, setActiveId)}
              {article.statusList && <OwnerStatusList />}
              {article.bodyAfter && renderArticleBody(article.bodyAfter, setActiveId)}
            </div>
            <div className="mt-6 rounded-lg border border-[#C2C9D1] bg-[#F7F8FA] px-4 py-3 text-xs text-[#6b7280]">
              <p className="mb-1"><a href={IRS_PUB_970} target="_blank" rel="noreferrer" className="font-semibold text-[#034AA2] hover:underline">IRS Publication 970 <ArrowSquareOut size={11} weight="bold" className="inline" /></a> — the authoritative source for education tax benefits.</p>
              <p>{DISCLAIMER}</p>
            </div>
          </article>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Help Center" subtitle="Answers to common questions about 529 plans, qualified expenses, and how to use this app." actions={<SetupGuideLink />} />
      <div className="mx-auto max-w-2xl space-y-2">
        {ARTICLES.map((a) => (
          <button key={a.id} onClick={() => setActiveId(a.id)} className="flex w-full items-start gap-3 rounded-lg border border-[#C2C9D1] bg-white px-4 py-3 text-left transition-colors hover:border-[#034AA2] hover:bg-[#F4F7FB]" data-testid={`help-link-${a.id}`}>
            <BookOpen size={20} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
            <div>
              <div className="text-sm font-bold text-[#0A0A0A]">{a.title}</div>
              <div className="mt-0.5 text-xs text-[#6b7280]">{a.summary}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function renderArticleBody(body, setActiveId) {
  return body.trim().split("\n\n").map((para, i) => {
    const t = para.trim();
    if (!t) return null;
    const render = (text) => renderBodyText(text, setActiveId);
    if (t.startsWith("## ")) return <h2 key={i} className="mt-6 text-lg font-bold text-[#0A0A0A]">{t.replace("## ", "")}</h2>;
    if (t.startsWith("### ")) return <h3 key={i} className="mt-4 text-base font-bold text-[#0A0A0A]">{t.replace("### ", "")}</h3>;
    if (t.startsWith("- ")) return <ul key={i} className="list-disc space-y-1 pl-5">{t.split("\n").map((line, j) => <li key={j}>{render(line.replace(/^- /, ""))}</li>)}</ul>;
    if (/^\d+\. /.test(t)) return <ol key={i} className="list-decimal space-y-1 pl-5">{t.split("\n").map((line, j) => <li key={j}>{render(line.replace(/^\d+\. /, ""))}</li>)}</ol>;
    return <p key={i}>{render(t)}</p>;
  });
}

// ZEN-315: the account owner's status definitions, rendered live from the
// Expenses screen's own list (LEGEND) with the app's own badge component. The
// wording here is the owner-approved wording (ZEN-113, ZEN-314) — it is not
// authored in this file and must not be.
function OwnerStatusList() {
  return (
    <ul className="mt-4 space-y-3" data-testid="help-owner-status-list">
      {LEGEND.map(({ k, desc }) => (
        <li key={k} className="flex items-start gap-2.5" data-testid={`help-status-${k}`}>
          <StatusChip status={k} full />
          <span>{desc}</span>
        </li>
      ))}
    </ul>
  );
}

function renderBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p);
}

function renderBodyText(text, onNavigate) {
  // Split on article links [[id|label]] and bold **text**
  const parts = text.split(/(\[\[[^\]]+\]\]|\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("[[") && p.endsWith("]]")) {
      const inner = p.slice(2, -2);
      const [id, label] = inner.includes("|") ? inner.split("|", 2) : [inner, inner];
      return <button key={i} onClick={() => onNavigate?.(id)} className="font-semibold text-[#034AA2] hover:underline">{label}</button>;
    }
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    return p;
  });
}
